import createTxpDataRow from './createTxpDataRow';
import ActivityType from '../../schema/innerSchema/ActivityType';
import CardItem from '../../schema/viewSchema/CardItem';
import DetailsIcons from '../../schema/configSchema/DetailsIcons';
import EntityType from '../../schema/innerSchema/EntityType';
import ParcelDelivery from '../../schema/ParcelDelivery';
import ActionNameConstants from '../../helpers/ActionNameConstants';
import TxpStrings from '../../schema/configSchema/TxpStrings';
import isSameDay from 'date-fns/is_same_day';
import TxpDateTimeCallbacks from 'txp-data/lib/schema/configSchema/TxpDateTimeCallbacks';

/**
 * Extracts the Parcel Delivery specific data needed for displaying the card
 */

export default function createParcelDeliveryCard(
    parcelDeliveryEntity: ParcelDelivery,
    activityId: string,
    showLocation: boolean,
    entityImage: string,
    txpStrings: TxpStrings,
    entityIcon?: string,
    calendarEventId?: string,
    detailsIcons?: DetailsIcons,
    txpDateTimeCallbacks?: TxpDateTimeCallbacks
): CardItem {
    let partOfOrder = parcelDeliveryEntity.partOfOrder;

    // format lines for Summary
    let seller = partOfOrder.seller ? partOfOrder.seller.name : null;
    let orderNumber = partOfOrder ? partOfOrder.orderNumber : null;
    let summaryFirstLine = txpStrings.formatters.format(
        txpStrings.strings.parcelDeliveryFirstLine,
        seller
    );
    let summarySecondLine = txpStrings.formatters.format(
        txpStrings.strings.parcelDeliverySecondLine,
        orderNumber
    );

    // format the estimate time
    let latestDeliveryDate = parcelDeliveryEntity.expectedArrivalUntil;
    let earliestDeliveryDate = parcelDeliveryEntity.expectedArrivalFrom;

    const latestDeliveryDateString: string = latestDeliveryDate.toString();
    let deliveryText: string;
    let isTheSameDay: boolean;
    if (latestDeliveryDate) {
        if (txpDateTimeCallbacks && txpDateTimeCallbacks.isSameDay) {
            isTheSameDay = txpDateTimeCallbacks.isSameDay(latestDeliveryDate, earliestDeliveryDate);
        } else {
            isTheSameDay = isSameDay(latestDeliveryDate, earliestDeliveryDate);
        }
        if (earliestDeliveryDate && !isTheSameDay) {
            deliveryText = txpStrings.formatters.format(
                txpStrings.strings.deliveryTextRange,
                txpStrings.formatters.formatWeekDayDate(earliestDeliveryDate.toString()),
                txpStrings.formatters.formatWeekDayDate(latestDeliveryDateString)
            );
        } else {
            deliveryText = txpStrings.formatters.formatWeekDayDate(latestDeliveryDateString);
        }
    }

    // format lines for Details
    let trackingNumber = parcelDeliveryEntity.trackingNumber;
    let provider = parcelDeliveryEntity.provider ? parcelDeliveryEntity.provider.name : null;
    let orderedItem = partOfOrder ? partOfOrder.orderedItem : null;
    let itemText = partOfOrder && orderedItem && orderedItem.name ? orderedItem.name : null;
    let trackingText =
        trackingNumber && provider
            ? txpStrings.formatters.format(
                  txpStrings.strings.trackingText,
                  provider,
                  trackingNumber
              )
            : null;
    let address = parcelDeliveryEntity.deliveryAddress;
    let addressText = address && address.streetAddress ? address.streetAddress : null;
    addressText = showLocation ? addressText : null;
    const actionButtonData = parcelDeliveryEntity.trackingUrl
        ? {
              actionIconName: detailsIcons.deliveryTruck,
              actionUrl: parcelDeliveryEntity.trackingUrl,
              actionButtonText: txpStrings.strings.trackPackageButtonText,
              actionName: ActionNameConstants.TrackPackage,
          }
        : null;

    return {
        id: activityId,
        entityImage: entityImage,
        entityIcon: entityIcon,
        activityType: ActivityType.ParcelDelivery,
        entityType: EntityType.ParcelDelivery,
        summaryData: {
            firstLine: { text: summaryFirstLine },
            secondLine: { text: summarySecondLine },
        },
        detailsData: {
            firstLine: createTxpDataRow(itemText, null, entityIcon), // Need clarity from designer on the icon to use
            secondLine: createTxpDataRow(trackingText, null, detailsIcons.deliveryTruck), // Need clarity from designer on the icon to use
            thirdLine: createTxpDataRow(
                deliveryText,
                txpStrings.strings.deliveryText,
                detailsIcons.clock
            ),
            fourthLine: createTxpDataRow(
                addressText,
                null,
                detailsIcons.location,
                false /* is required */,
                null /* placeholder */,
                true /* includeToolTip */
            ),
        },
        brandsSummaryFirstLine: summaryFirstLine,
        brandsSummarySecondLine: txpStrings.formatters.format(
            txpStrings.strings.brandSummaryPackageSecondLineText,
            txpStrings.formatters.formatWeekDayDate(latestDeliveryDateString)
        ),
        actionButtonData: actionButtonData,
        calendarEventId: calendarEventId,
    } as CardItem;
}
